import { LoadingButton } from '@mui/lab';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { ICompanyContactLinkListItem } from 'src/services/connections/connections.types';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import ContactsNotesModal from 'src/components/custom-tables/contacts-table/contacts-notes-modal';

// ----------------------------------------------------------------------

interface ContactLinkRowActionsProps {
  row: ICompanyContactLinkListItem;
  /** Opens the context-preserving record panel rather than navigating away from the company. */
  onView: (contactId: string) => void;
  onUnlink: (row: ICompanyContactLinkListItem) => void;
  isUnlinking?: boolean;
}

/**
 * Row kebab for a company's linked contacts: View, Notes, and Unlink.
 *
 * Deliberately NOT ContactRowActions, even though the row types are compatible. That component's
 * Remove deletes the contact outright; here the destructive action only severs this company's link
 * and the contact survives. Sharing one component would put two very different consequences behind
 * the same menu item.
 */
export default function ContactLinkRowActions({ row, onView, onUnlink, isUnlinking }: ContactLinkRowActionsProps) {
  const { t } = useTranslate();
  const popover = usePopover();

  const notes = useBoolean();
  const confirm = useBoolean();

  return (
    <>
      <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
        <Iconify icon="eva:more-horizontal-fill" />
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow="right-top" sx={{ width: 200 }}>
        <MenuItem onClick={() => { popover.onClose(); onView(row.id); }}>
          <Iconify icon="mdi:eye" />
          <Typography variant="body2">{t('common.view')}</Typography>
        </MenuItem>

        {row.thread_id && (
          <MenuItem onClick={() => { popover.onClose(); notes.onTrue(); }}>
            <Iconify icon="mdi:comments" />
            <Typography variant="body2">{t('common.notes')}</Typography>
          </MenuItem>
        )}

        <MenuItem sx={{ color: 'error.main' }} onClick={() => { popover.onClose(); confirm.onTrue(); }}>
          <Iconify icon="eva:trash-2-outline" />
          <Typography variant="body2">{t('common.remove')}</Typography>
        </MenuItem>
      </CustomPopover>

      {row.thread_id && (
        <ContactsNotesModal threadId={row.thread_id} open={notes.value} onClose={notes.onFalse} />
      )}

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title={t('common.remove')}
        content={
          <Typography variant="body2">
            {t('companies.profile.linked_contacts.unlink_confirm_message')}
          </Typography>
        }
        action={
          <LoadingButton
            loading={isUnlinking}
            variant="contained"
            color="error"
            onClick={() => { onUnlink(row); confirm.onFalse(); }}
          >
            {t('common.remove')}
          </LoadingButton>
        }
      />
    </>
  );
}
